/**
 * Turn an opaque/empty auth-provider error message into human-facing copy.
 *
 * Extracted to its own module (like emailConfirmationMatch.ts) so the Vitest
 * unit test can target the pure function without rendering React.
 *
 * Why this exists: supabase-js (@supabase/auth-js) derives an AuthApiError
 * message via `err.msg || err.message || err.error_description || err.error ||
 * JSON.stringify(err)`. When GoTrue returns an empty/opaque error body (e.g. a
 * `{}` from a 5xx/timeout at the edge), that last fallback yields the literal
 * string `"{}"`, which the login modal would otherwise render verbatim to the
 * user. This maps such non-human blobs to a friendly fallback while faithfully
 * preserving genuinely-informative provider messages (rate limits, invalid
 * credentials, etc. — per the passthrough / faithful-error principle).
 *
 * @param message  the provider error's `.message` (or a caught Error message)
 * @param fallback human-friendly copy to show when `message` is not readable
 */
export function humanizeAuthError(message: string | null | undefined, fallback: string): string {
  const trimmed = (message ?? '').trim()
  if (trimmed === '') return fallback

  const lowered = trimmed.toLowerCase()
  // Non-JSON opaque literals that JSON.parse can't catch below.
  if (lowered === 'undefined' || lowered === '[object object]') return fallback

  // A genuine human-readable message is never valid JSON. auth-js falls back to
  // JSON.stringify() for opaque/empty error bodies, so anything that parses to a
  // non-string value ('{}', '[]', 'null', or a raw diagnostic blob like
  // '{"code":"..."}') is an unreadable blob, not user copy — show the fallback.
  // A normal message (e.g. 'Email rate limit exceeded', '[auth] slow down')
  // fails to parse and is kept verbatim.
  // typeof-guard the parse result directly (no annotation) — the repo's
  // forbidden-syntax lint bans `any`/`unknown`/`as`.
  try {
    if (typeof JSON.parse(trimmed) !== 'string') return fallback
  } catch {
    // Not JSON — a normal human message. Keep it.
  }

  return trimmed
}

// ---------------------------------------------------------------------------
// Auth-error classification: "contact the dreamwrite team" for UNEXPECTED errors
// ---------------------------------------------------------------------------
//
// Product requirement: on the three unauthenticated auth surfaces (login,
// signup, password reset) any UNEXPECTED failure — server 5xx, network/CORS,
// opaque body, or an unrecognized code the user cannot self-correct — must show
// a single support message instead of leaking a raw GoTrue string. EXPECTED /
// user-actionable failures (wrong password, unconfirmed email, invalid email,
// disallowed domain, weak/same password, rate limit, expired reset link) keep
// their specific copy so the user can fix the input.
//
// This is UI *display* mapping, not error swallowing (CLAUDE.md #7): every
// caller still logs the raw provider error (status/code/name/message) under the
// `dw:auth-error-map` feature tag before calling in here.
//
// This module no longer stores user-facing copy. It returns a stable
// AuthErrorKind (a code) plus the i18n key for it (AUTH_ERROR_COPY_KEYS, below);
// the display strings live in the `auth` i18n namespace for every UI locale and are
// resolved at the UI boundary via authErrorText(t, …) — see #2433. `passthrough`
// still shows the provider's own message (faithful-error, CLAUDE.md #7).

export type AuthErrorKind =
  | 'invalid_credentials'
  | 'email_not_confirmed'
  | 'email_invalid'
  | 'domain_not_allowed'
  | 'passthrough' // recognized + human-readable provider message → show verbatim
  | 'recovery_link_invalid'
  | 'contact_team' // UNEXPECTED → localized contact-team copy

export type AuthErrorCtx =
  | 'signin'
  | 'signup-standard'
  | 'signup-admin'
  | 'reset-request'
  | 'reset-update'
  | 'recovery-verify'
  | 'oauth'

export interface NormalizedAuthError {
  status?: number
  code?: string
  message: string
}

// Pull { status, code, message } off an AuthError, a REST body ({status,detail})
// or any thrown object, using runtime guards only (no `as`/`unknown` — banned).
// Accepts `object` so both a typed AuthError and a caught `err instanceof Object
// ? err : {}` narrow cleanly at the callsite.
export function normalizeAuthError(error: object): NormalizedAuthError {
  const status =
    'status' in error && typeof error.status === 'number' ? error.status : undefined
  const code = 'code' in error && typeof error.code === 'string' ? error.code : undefined
  // Prefer `.message` (AuthError / Error); fall back to `.detail` (backend REST
  // body) then `.error_description` (legacy GoTrue redirect param).
  let message = ''
  if ('message' in error && typeof error.message === 'string') {
    message = error.message
  } else if ('detail' in error && typeof error.detail === 'string') {
    message = error.detail
  } else if ('error_description' in error && typeof error.error_description === 'string') {
    message = error.error_description
  }
  return { status, code, message }
}

function includesAny(haystack: string, needles: string[]): boolean {
  const lowered = haystack.toLowerCase()
  return needles.some((needle) => lowered.includes(needle))
}

function isRateLimited(n: NormalizedAuthError): boolean {
  if (n.status === 429) return true
  if (n.code === 'over_email_send_rate_limit' || n.code === 'over_request_rate_limit') return true
  return includesAny(n.message, ['rate limit', 'for security purposes, you can only request'])
}

function isEmailInvalid(n: NormalizedAuthError): boolean {
  if (n.code === 'email_address_invalid') return true
  const emailish = includesAny(n.message, [
    'unable to validate email address',
    'invalid format',
    'invalid email',
  ])
  if (n.code === 'validation_failed' && emailish) return true
  return emailish
}

function isRecoveryLinkInvalid(n: NormalizedAuthError): boolean {
  if (
    n.code === 'otp_expired' ||
    n.code === 'invalid_token' ||
    n.code === 'access_denied' ||
    n.code === 'otp_disabled'
  ) {
    return true
  }
  return includesAny(n.message, [
    'expired or is invalid',
    'invalid or has expired',
    'token has expired',
  ])
}

// Classify a provider error for a given surface. Returns a discriminant; the
// caller maps it to copy (English constant, i18n `t()`, or the raw message for
// `passthrough`). Unknown / system / network failures → 'contact_team'.
export function classifyAuthError(error: object | null | undefined, ctx: AuthErrorCtx): AuthErrorKind {
  if (!error) return 'contact_team'
  const n = normalizeAuthError(error)

  // A genuine, human-readable message is a prerequisite for `passthrough`; an
  // opaque blob ('{}', etc.) must never be shown verbatim.
  const readable = humanizeAuthError(n.message, '') !== ''

  // Recovery-link verification (fresh verifyOtp or a legacy redirect error).
  if (ctx === 'recovery-verify') {
    if (isRecoveryLinkInvalid(n)) return 'recovery_link_invalid'
    // A rate limit while verifying is actionable (wait + retry) — keep it.
    if (isRateLimited(n) && readable) return 'passthrough'
    return 'contact_team'
  }

  if (ctx === 'signin') {
    if (n.code === 'invalid_credentials' || includesAny(n.message, ['invalid login credentials'])) {
      return 'invalid_credentials'
    }
    if (n.code === 'email_not_confirmed' || includesAny(n.message, ['email not confirmed'])) {
      return 'email_not_confirmed'
    }
    if (isEmailInvalid(n)) return 'email_invalid'
    if (isRateLimited(n) && readable) return 'passthrough'
    return 'contact_team'
  }

  if (ctx === 'signup-standard' || ctx === 'signup-admin') {
    if (
      n.code === 'email_address_not_authorized' ||
      includesAny(n.message, ['email domain not allowed', 'database error saving new user'])
    ) {
      return 'domain_not_allowed'
    }
    if (isEmailInvalid(n)) return 'email_invalid'
    if (n.code === 'weak_password' || (n.status === 422 && includesAny(n.message, ['password']))) {
      return readable ? 'passthrough' : 'contact_team'
    }
    if (isRateLimited(n) && readable) return 'passthrough'
    // NOTE: `user_already_exists` is intentionally NOT handled — revealing it
    // would enable account enumeration (CWE-204). The standard-signup caller
    // already treats the enumeration-safe empty-identities response as success.
    return 'contact_team'
  }

  if (ctx === 'reset-request') {
    if (isEmailInvalid(n)) return 'email_invalid'
    if (isRateLimited(n) && readable) return 'passthrough'
    return 'contact_team'
  }

  if (ctx === 'reset-update') {
    if (n.code === 'same_password' || includesAny(n.message, ['should be different'])) {
      return readable ? 'passthrough' : 'contact_team'
    }
    if (n.code === 'weak_password' || (n.status === 422 && includesAny(n.message, ['password']))) {
      return readable ? 'passthrough' : 'contact_team'
    }
    if (isRateLimited(n) && readable) return 'passthrough'
    return 'contact_team'
  }

  // oauth: setup failures are almost always network/provider/system.
  if (isRateLimited(n) && readable) return 'passthrough'
  return 'contact_team'
}

// i18n keys (in the `auth` namespace) for each non-passthrough kind. Fully
// qualified (`auth:…`) so any caller's `t` resolves them regardless of its own
// default namespace. `recovery_link_invalid` reuses the existing reset-link copy.
// `passthrough` intentionally has no key — it shows the provider's own message.
export const AUTH_ERROR_COPY_KEYS: Record<Exclude<AuthErrorKind, 'passthrough'>, string> = {
  invalid_credentials: 'auth:errors.invalidCredentials',
  email_not_confirmed: 'auth:errors.emailNotConfirmed',
  email_invalid: 'auth:errors.emailInvalid',
  domain_not_allowed: 'auth:errors.domainNotAllowed',
  recovery_link_invalid: 'auth:resetLinkInvalidOrExpired',
  contact_team: 'auth:errors.contactTeam',
}

// Resolve a classified kind to localized user copy at the UI boundary. `t` is the
// react-i18next translator, passed in so this module stays free of React/hooks
// (same pattern as translatePasswordError). `passthrough` shows the recognized
// provider message verbatim (faithful-error), degrading to the localized
// contact-team copy only if that message is opaque.
export function authErrorText(
  t: (key: string, opts?: Record<string, string | number>) => string,
  kind: AuthErrorKind,
  rawMessage: string,
): string {
  if (kind === 'passthrough') {
    return humanizeAuthError(rawMessage, t('auth:errors.contactTeam'))
  }
  return t(AUTH_ERROR_COPY_KEYS[kind])
}

// Convenience for the auth surfaces: classify + localize in one call (the
// localized successor to the former English-returning describeAuthError).
export function describeAuthError(
  t: (key: string, opts?: Record<string, string | number>) => string,
  error: object | null | undefined,
  ctx: AuthErrorCtx,
): string {
  const kind = classifyAuthError(error, ctx)
  const message = error ? normalizeAuthError(error).message : ''
  return authErrorText(t, kind, message)
}
